import React from 'react';
import { useTranslation } from 'react-i18next';
import FilingProgressBar from './FilingProgressBar';
import './ApplicationStatusCard.css';

const STATUS_STEPS = [
  { key: 'payment_completed', label: 'Payment complete' },
  { key: 'documents_generated', label: 'Documents generated' },
  { key: 'filing_in_progress', label: 'Filing in progress' },
  { key: 'registered', label: 'Registration' }
];

function ApplicationStatusCard({ application }) {
  const { t } = useTranslation('notifications');

  if (!application) return null;

  const { status, filings = [], applicationId, trademarkName } = application;

  // Determine which steps are complete based on status
  const getStepStatus = (stepKey) => {
    const statusMap = {
      'payment_completed': ['payment_completed', 'pending_attorney_filing', 'filing_in_progress', 'filed', 'registered'],
      'documents_generated': ['pending_attorney_filing', 'filing_in_progress', 'filed', 'registered'],
      'filing_in_progress': ['filing_in_progress', 'filed', 'registered'],
      'registered': ['registered']
    };

    const completedStatuses = statusMap[stepKey] || [];
    return completedStatuses.includes(status) ? 'completed' : 'pending';
  };

  // Check if all filings are complete
  const allFilingsComplete = filings.length > 0 && filings.every(f => f.status === 'filed' || f.status === 'registered');
  const someFilingsComplete = filings.some(f => f.status === 'filed' || f.status === 'registered');

  // Get overall status display
  const getStatusDisplay = () => {
    if (status === 'registered' || allFilingsComplete) {
      return { text: t('status.registered', 'Registered'), color: 'success' };
    }
    if (status === 'filing_in_progress' || someFilingsComplete) {
      return { text: t('status.filingInProgress', 'Filing in Progress'), color: 'warning' };
    }
    if (status === 'pending_attorney_filing') {
      return { text: t('status.pendingFiling', 'Pending Filing'), color: 'info' };
    }
    if (status === 'payment_completed') {
      return { text: t('status.paymentComplete', 'Payment Complete'), color: 'info' };
    }
    return { text: t('status.inProgress', 'In Progress'), color: 'default' };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="application-status-card">
      <div className="application-status-header">
        <div className="application-info">
          <span className="application-id">{applicationId || application.id?.slice(0, 8)}</span>
          {trademarkName && <span className="trademark-name">{trademarkName}</span>}
        </div>
        <span className={`status-badge status-${statusDisplay.color}`}>
          {statusDisplay.text}
        </span>
      </div>

      <div className="status-progress">
        {STATUS_STEPS.map((step, index) => {
          const stepStatus = getStepStatus(step.key);
          const isActive = stepStatus === 'completed';
          const isCurrent = index === STATUS_STEPS.findIndex(s => getStepStatus(s.key) === 'pending') - 1;

          return (
            <div
              key={step.key}
              className={`status-step ${isActive ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
            >
              <div className="step-indicator">
                {isActive ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                ) : (
                  <span className="step-number">{index + 1}</span>
                )}
              </div>
              <span className="step-label">{t(`steps.${step.key}`, step.label)}</span>
              {index < STATUS_STEPS.length - 1 && <div className="step-connector" />}
            </div>
          );
        })}
      </div>

      {filings.length > 0 && (
        <div className="filings-section">
          <h4 className="filings-title">{t('filings.title', 'Filing Status by Jurisdiction')}</h4>
          <div className="filings-list">
            {filings.map((filing, index) => (
              <FilingProgressBar
                key={filing.jurisdiction || index}
                filing={filing}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationStatusCard;
